import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Avatar,
  LinearProgress,
  Container,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  ArrowBack,
  Assessment,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Security,
  Psychology,
  PhotoCamera,
  ThumbUp,
  ThumbDown,
  Flag,
  Schedule,
  AttachMoney,
  Store,
  Category,
  Business,
  TrendingUp,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const ComparisonCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: 300,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  backgroundColor: theme.palette.grey[100],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

interface AnalysisDetails {
  id: number;
  analysis_id: string;
  reference_product_id: number;
  test_product_name: string;
  test_product_seller?: string;
  test_product_url?: string;
  test_product_price?: number;
  is_counterfeit: boolean;
  confidence_score: number;
  risk_level: string;
  status: string;
  created_at: string;
  processing_time?: number;
  image_similarity_score?: number;
  text_similarity_score?: number;
  llm_analysis_score?: number;
  key_differences?: string[];
  recommendations?: string[];
  detailed_analysis?: any;
  test_image_urls?: string[];
  comparison_image_urls?: string[];
  test_image?: string;
  reference_product?: {
    id: number;
    name: string;
    brand_name: string;
    category_name: string;
    primary_image_url?: string;
    sku?: string;
    price_range_min?: number;
    price_range_max?: number;
    manufacturer?: string;
    country_of_origin?: string;
  };
}

const AnalysisDetailsPage: React.FC = () => {
  const { analysisId } = useParams<{ analysisId: string }>();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState<AnalysisDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject' | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    if (analysisId) {
      loadAnalysisDetails();
    }
  }, [analysisId]);

  const loadAnalysisDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`https://server.bitsindia.in/api/analyses/${analysisId}/`, {
        headers: {
          'Authorization': 'Bearer demo-token',
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        
        // Ensure key_differences and recommendations are arrays
        if (data.key_differences && typeof data.key_differences === 'string') {
          try {
            data.key_differences = JSON.parse(data.key_differences);
          } catch (e) {
            data.key_differences = [];
          }
        }
        
        if (data.recommendations && typeof data.recommendations === 'string') {
          try {
            data.recommendations = JSON.parse(data.recommendations);
          } catch (e) {
            data.recommendations = [];
          }
        }
        
        // Ensure they are arrays
        if (!Array.isArray(data.key_differences)) {
          data.key_differences = [];
        }
        if (!Array.isArray(data.recommendations)) {
          data.recommendations = [];
        }
        
        // Fix test_image_urls and comparison_image_urls
        if (data.test_image_urls && typeof data.test_image_urls === 'string') {
          try {
            data.test_image_urls = JSON.parse(data.test_image_urls);
          } catch (e) {
            data.test_image_urls = [];
          }
        }
        
        if (data.comparison_image_urls && typeof data.comparison_image_urls === 'string') {
          try {
            data.comparison_image_urls = JSON.parse(data.comparison_image_urls);
          } catch (e) {
            data.comparison_image_urls = [];
          }
        }
        
        // Ensure image arrays are arrays
        if (!Array.isArray(data.test_image_urls)) {
          data.test_image_urls = [];
        }
        if (!Array.isArray(data.comparison_image_urls)) {
          data.comparison_image_urls = [];
        }
        
        setAnalysis(data);
      } else {
        throw new Error('Failed to load analysis details');
      }
    } catch (err: any) {
      console.error('Error loading analysis details:', err);
      setError(err.message || 'Failed to load analysis details');
    } finally {
      setLoading(false);
    }
  };

  const handleReviewAction = (action: 'approve' | 'reject') => {
    setReviewAction(action);
    setReviewDialogOpen(true);
  };

  const submitReview = async () => {
    if (!analysis || !reviewAction) return;

    try {
      setSubmittingReview(true);

      const response = await fetch(`https://server.bitsindia.in/api/review/${analysis.analysis_id}/decision/`, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer demo-token',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          decision: reviewAction === 'approve' ? 'approved' : 'rejected',
          confidence: 0.9,
          notes: reviewNotes,
          time_spent_minutes: 5,
          flagged_differences: []
        }),
      });

      if (response.ok) {
        // Update local state
        setAnalysis(prev => prev ? { ...prev, status: reviewAction === 'approve' ? 'APPROVED' : 'REJECTED' } : null);
        setReviewDialogOpen(false);
        setReviewNotes('');
        setReviewAction(null);
        
        // Show success message
        setError(`Analysis ${reviewAction}d successfully!`);
        setTimeout(() => setError(null), 3000);
      } else {
        throw new Error(`Failed to ${reviewAction} analysis`);
      }
    } catch (err: any) {
      console.error('Error submitting review:', err);
      setError(err.message || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending_review': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk.toLowerCase()) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getRiskIcon = (riskLevel: string) => {
    switch (riskLevel.toLowerCase()) {
      case 'high': return <ErrorIcon color="error" />;
      case 'medium': return <Warning color="warning" />;
      case 'low': return <CheckCircle color="success" />;
      default: return <Security />;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Loading analysis details...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error && !analysis) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/analyses')}>
          Back to Analyses
        </Button>
      </Container>
    );
  }

  if (!analysis) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" color="text.secondary">
          Analysis not found
        </Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/analyses')}>
          Back to Analyses
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/analyses')}
          sx={{ mb: 2 }}
        >
          Back to Analyses
        </Button>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <Assessment />
          </Avatar>
          <Box>
            <Typography variant="h3" component="h1" fontWeight="bold" color="primary">
              Analysis Details
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {analysis.analysis_id}
            </Typography>
          </Box>
        </Box>

        {/* Error/Success Alert */}
        {error && (
          <Alert
            severity={error.includes('successfully') ? 'success' : 'error'}
            sx={{ mb: 3 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
      </Box>

      {/* Analysis Summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: analysis.is_counterfeit ? 'error.main' : 'success.main' }}>
                  {analysis.is_counterfeit ? <Security /> : <CheckCircle />}
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {analysis.is_counterfeit ? 'Counterfeit' : 'Authentic'}
                  </Typography>
                  <Typography color="text.secondary">Detection Result</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <TrendingUp />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {(analysis.confidence_score * 100).toFixed(1)}%
                  </Typography>
                  <Typography color="text.secondary">Confidence</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: getRiskColor(analysis.risk_level) + '.main' }}>
                  {getRiskIcon(analysis.risk_level)}
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {analysis.risk_level}
                  </Typography>
                  <Typography color="text.secondary">Risk Level</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: getStatusColor(analysis.status) + '.main' }}>
                  <Schedule />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {analysis.status.replace('_', ' ')}
                  </Typography>
                  <Typography color="text.secondary">Status</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Side-by-Side Comparison */}
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Product Comparison
      </Typography>
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Test Product */}
        <Grid item xs={12} md={6}>
          <ComparisonCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <PhotoCamera />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Test Product
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Product being analyzed
                  </Typography>
                </Box>
              </Box>
              
              <ImageContainer>
                {analysis.test_image_urls && analysis.test_image_urls.length > 0 ? (
                  <img
                    src={`https://server.bitsindia.in${analysis.test_image_urls[0]}`}
                    alt="Test Product"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      console.error('Failed to load test image:', analysis.test_image_urls?.[0]);
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : analysis.test_image ? (
                  <img
                    src={`https://server.bitsindia.in${analysis.test_image}`}
                    alt="Test Product"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      console.error('Failed to load test image:', analysis.test_image);
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <PhotoCamera sx={{ fontSize: 64, color: 'text.secondary' }} />
                )}
              </ImageContainer>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {analysis.test_product_name}
                </Typography>
                
                <Stack spacing={1}>
                  {analysis.test_product_seller && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Store sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Seller: {analysis.test_product_seller}
                      </Typography>
                    </Box>
                  )}
                  
                  {analysis.test_product_price && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AttachMoney sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Price: ${analysis.test_product_price}
                      </Typography>
                    </Box>
                  )}
                  
                  {analysis.test_product_url && (
                    <Typography variant="body2" color="primary">
                      <a href={analysis.test_product_url} target="_blank" rel="noopener noreferrer">
                        View Product URL
                      </a>
                    </Typography>
                  )}
                </Stack>
              </Box>
            </CardContent>
          </ComparisonCard>
        </Grid>

        {/* Reference Product */}
        <Grid item xs={12} md={6}>
          <ComparisonCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <CheckCircle />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Reference Product
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Authentic product for comparison
                  </Typography>
                </Box>
              </Box>
              
              <ImageContainer>
                {analysis.reference_product?.primary_image_url ? (
                  <img
                    src={`https://server.bitsindia.in${analysis.reference_product.primary_image_url}`}
                    alt="Reference Product"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <CheckCircle sx={{ fontSize: 64, color: 'text.secondary' }} />
                )}
              </ImageContainer>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {analysis.reference_product?.name || 'Unknown Product'}
                </Typography>
                
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Brand: {analysis.reference_product?.brand_name || 'Unknown'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Category sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Category: {analysis.reference_product?.category_name || 'Unknown'}
                    </Typography>
                  </Box>
                  
                  {analysis.reference_product?.sku && (
                    <Typography variant="body2" color="text.secondary">
                      SKU: {analysis.reference_product.sku}
                    </Typography>
                  )}
                  
                  {analysis.reference_product?.price_range_min && analysis.reference_product?.price_range_max && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AttachMoney sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Price Range: ${analysis.reference_product.price_range_min} - ${analysis.reference_product.price_range_max}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            </CardContent>
          </ComparisonCard>
        </Grid>
      </Grid>

      {/* Analysis Details */}
      <Grid container spacing={3}>
        {/* Component Scores */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Component Scores
              </Typography>
              
              <Stack spacing={2}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Image Similarity</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {analysis.image_similarity_score && analysis.image_similarity_score > 0 
                        ? (analysis.image_similarity_score * 100).toFixed(1) + '%' 
                        : 'N/A'}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={analysis.image_similarity_score && analysis.image_similarity_score > 0 
                      ? analysis.image_similarity_score * 100 
                      : 0}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
                
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Text Similarity</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {analysis.text_similarity_score && analysis.text_similarity_score > 0 
                        ? (analysis.text_similarity_score * 100).toFixed(1) + '%' 
                        : 'N/A'}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={analysis.text_similarity_score && analysis.text_similarity_score > 0 
                      ? analysis.text_similarity_score * 100 
                      : 0}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
                
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">LLM Analysis</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {analysis.llm_analysis_score && analysis.llm_analysis_score > 0 
                        ? (analysis.llm_analysis_score * 100).toFixed(1) + '%' 
                        : 'N/A'}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={analysis.llm_analysis_score && analysis.llm_analysis_score > 0 
                      ? analysis.llm_analysis_score * 100 
                      : 0}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Key Differences */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Key Differences Detected
              </Typography>
              
              {analysis.key_differences && Array.isArray(analysis.key_differences) && analysis.key_differences.length > 0 ? (
                <List dense>
                  {analysis.key_differences.map((difference, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Flag color="warning" />
                      </ListItemIcon>
                      <ListItemText primary={difference} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No significant differences detected
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recommendations */}
        {analysis.recommendations && Array.isArray(analysis.recommendations) && analysis.recommendations.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recommendations
                </Typography>
                
                <List>
                  {analysis.recommendations.map((recommendation, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Psychology color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={recommendation} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Processing Information */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Processing Information
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Analysis ID
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {analysis.analysis_id}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Processing Time
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {analysis.processing_time ? `${analysis.processing_time.toFixed(2)}s` : 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {new Date(analysis.created_at).toLocaleString()}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={analysis.status.replace('_', ' ')}
                    color={getStatusColor(analysis.status) as any}
                    size="small"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Review Actions */}
      {analysis.status === 'PENDING_REVIEW' && (
        <Box sx={{ 
          mt: 4, 
          display: 'flex', 
          gap: 4, 
          justifyContent: 'center', 
          flexWrap: 'wrap', 
          p: 3,
          backgroundColor: 'grey.50',
          borderRadius: 2
        }}>
          <Button
            variant="contained"
            color="success"
            size="large"
            startIcon={<ThumbUp />}
            onClick={() => handleReviewAction('approve')}
            sx={{ 
              minWidth: 200,
              maxWidth: 250,
              py: 2,
              px: 4,
              fontSize: '1rem',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
              textTransform: 'none',
              boxShadow: 3,
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-2px)'
              }
            }}
          >
            Approve Analysis
          </Button>
          <Button
            variant="contained"
            color="error"
            size="large"
            startIcon={<ThumbDown />}
            onClick={() => handleReviewAction('reject')}
            sx={{ 
              minWidth: 200,
              maxWidth: 250,
              py: 2,
              px: 4,
              fontSize: '1rem',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
              textTransform: 'none',
              boxShadow: 3,
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-2px)'
              }
            }}
          >
            Reject Analysis
          </Button>
        </Box>
      )}

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {reviewAction === 'approve' ? 'Approve' : 'Reject'} Analysis
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              {analysis.test_product_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Analysis ID: {analysis.analysis_id}
            </Typography>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Review Notes"
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            placeholder="Add your review notes here..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={submitReview}
            variant="contained"
            color={reviewAction === 'approve' ? 'success' : 'error'}
            disabled={submittingReview}
          >
            {submittingReview ? <CircularProgress size={20} /> : `${reviewAction === 'approve' ? 'Approve' : 'Reject'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AnalysisDetailsPage; 